"use client";

import { useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";

/* ------------------------------------------------------------------ */
/*  Question types and generation logic                               */
/* ------------------------------------------------------------------ */

type QuestionType = "descriptive" | "comparative" | "causal";

const QUESTION_TYPES: QuestionType[] = ["descriptive", "comparative", "causal"];

/** Display label paired with the fixed English value fed into the generated questions. */
interface DisciplineOption {
  id: keyof typeof RESEARCH_QUESTION_GENERATOR_STRINGS.disciplineLabels;
  en: string;
}

const DISCIPLINES: DisciplineOption[] = [
  { id: "psychology", en: "Psychology" },
  { id: "education", en: "Education" },
  { id: "sociology", en: "Sociology" },
  { id: "business", en: "Business" },
  { id: "healthSciences", en: "Health Sciences" },
  { id: "computerScience", en: "Computer Science" },
  { id: "environmentalScience", en: "Environmental Science" },
  { id: "politicalScience", en: "Political Science" },
  { id: "economics", en: "Economics" },
  { id: "history", en: "History" },
  { id: "literature", en: "Literature" },
  { id: "engineering", en: "Engineering" },
  { id: "biology", en: "Biology" },
  { id: "other", en: "Other" },
];

interface GeneratedQuestion {
  question: string;
  methodology: string;
}

function generateDescriptiveQuestions(
  topic: string,
  discipline: string
): GeneratedQuestion[] {
  const t = topic || "[your topic]";
  const d = discipline || "the field";
  return [
    {
      question: `What are the key characteristics of ${t} as observed in ${d}?`,
      methodology: "Qualitative survey or interview study",
    },
    {
      question: `How do practitioners in ${d} currently perceive ${t}?`,
      methodology: "Cross-sectional survey or phenomenological study",
    },
    {
      question: `What is the prevalence of ${t} among populations studied in ${d}?`,
      methodology: "Descriptive quantitative survey",
    },
    {
      question: `What factors contribute to ${t} according to current ${d} research?`,
      methodology: "Systematic literature review or meta-analysis",
    },
  ];
}

function generateComparativeQuestions(
  topic: string,
  discipline: string
): GeneratedQuestion[] {
  const t = topic || "[your topic]";
  const d = discipline || "the field";
  return [
    {
      question: `How does ${t} differ between experienced and novice practitioners in ${d}?`,
      methodology: "Comparative cross-sectional study",
    },
    {
      question: `What are the differences in outcomes related to ${t} across different demographic groups in ${d}?`,
      methodology: "Quantitative comparative analysis",
    },
    {
      question: `How do approaches to ${t} in ${d} compare across different institutional contexts?`,
      methodology: "Multi-site comparative case study",
    },
    {
      question: `Is there a significant difference in ${t} between those who receive an intervention and those who do not in ${d}?`,
      methodology: "Quasi-experimental comparative study",
    },
  ];
}

function generateCausalQuestions(
  topic: string,
  discipline: string
): GeneratedQuestion[] {
  const t = topic || "[your topic]";
  const d = discipline || "the field";
  return [
    {
      question: `What is the effect of ${t} on measurable outcomes in ${d}?`,
      methodology: "Randomized controlled experiment",
    },
    {
      question: `To what extent does ${t} influence long-term results in ${d}?`,
      methodology: "Longitudinal cohort study",
    },
    {
      question: `Does exposure to ${t} cause changes in behavior among participants studied in ${d}?`,
      methodology: "Experimental or quasi-experimental design",
    },
    {
      question: `What is the causal relationship between ${t} and related variables in ${d}?`,
      methodology: "Structural equation modeling or path analysis",
    },
  ];
}

function generateQuestions(
  topic: string,
  discipline: string,
  questionType: QuestionType
): GeneratedQuestion[] {
  switch (questionType) {
    case "descriptive":
      return generateDescriptiveQuestions(topic, discipline);
    case "comparative":
      return generateComparativeQuestions(topic, discipline);
    case "causal":
      return generateCausalQuestions(topic, discipline);
    default:
      return [];
  }
}

/* ------------------------------------------------------------------ */
/*  Page Component                                                    */
/* ------------------------------------------------------------------ */

/** English strings. A translated content file overrides these through `ui`. */
export const RESEARCH_QUESTION_GENERATOR_STRINGS = {
  researchTopic: "Research Topic",
  eGRemoteWorkProductivity:
    "e.g., remote work productivity, climate change adaptation",
  fieldDiscipline: "Field / Discipline",
  selectADiscipline: "Select a discipline...",
  questionType: "Question Type",
  generateResearchQuestions: "Generate Research Questions",
  generatedResearchQuestions: "Generated Research Questions",
  rq: "RQ",
  copied: "Copied!",
  copy: "Copy",
  suggestedMethodology: "Suggested methodology: ",
  questionTypeLabels: {
    descriptive: "Descriptive",
    comparative: "Comparative",
    causal: "Causal / Experimental",
  },
  questionTypeDescriptions: {
    descriptive:
      "Explores what, who, or how much. Ideal for surveys and observational studies.",
    comparative: "Examines differences between groups or variables.",
    causal: "Investigates cause-and-effect relationships between variables.",
  },
  disciplineLabels: {
    psychology: "Psychology",
    education: "Education",
    sociology: "Sociology",
    business: "Business",
    healthSciences: "Health Sciences",
    computerScience: "Computer Science",
    environmentalScience: "Environmental Science",
    politicalScience: "Political Science",
    economics: "Economics",
    history: "History",
    literature: "Literature",
    engineering: "Engineering",
    biology: "Biology",
    other: "Other",
  },
} as const;

export default function ResearchQuestionGeneratorWidget() {
  const [topic, setTopic] = useState("");
  const [discipline, setDiscipline] = useState("");
  const [questionType, setQuestionType] = useState<QuestionType>("descriptive");
  const [results, setResults] = useState<GeneratedQuestion[] | null>(null);
  const [copied, setCopied] = useState<number | null>(null);

  function handleGenerate() {
    const questions = generateQuestions(topic, discipline, questionType);
    setResults(questions);
    setCopied(null);
  }

  async function handleCopy(index: number, text: string) {
    if (!(await writeToClipboard(text))) {
      return;
    }
    setCopied(index);
    setTimeout(() => setCopied(null), 2000);
  }

  const strings = useToolUi(RESEARCH_QUESTION_GENERATOR_STRINGS);

  return (
    <>
      {/* Question Type Cards */}
      <div className="grid gap-3 sm:grid-cols-3">
        {QUESTION_TYPES.map((qt) => (
          <div className="rounded-lg border border-border bg-card p-4" key={qt}>
            <p className="font-medium text-foreground text-sm">
              {strings.questionTypeLabels[qt]}
            </p>
            <p className="mt-1 text-muted-foreground text-xs leading-relaxed">
              {strings.questionTypeDescriptions[qt]}
            </p>
          </div>
        ))}
      </div>
      {/* Form */}
      <div className="space-y-5 rounded-lg border border-border bg-card p-6">
        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="rqg-topic"
          >
            {strings.researchTopic}
          </label>
          <input
            className="w-full rounded-lg border border-border bg-background p-2.5 text-foreground text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            id="rqg-topic"
            onChange={(e) => setTopic(e.target.value)}
            placeholder={strings.eGRemoteWorkProductivity}
            type="text"
            value={topic}
          />
        </div>

        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="rqg-discipline"
          >
            {strings.fieldDiscipline}
          </label>
          <select
            className="w-full rounded-lg border border-border bg-background p-2.5 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="rqg-discipline"
            onChange={(e) => setDiscipline(e.target.value)}
            value={discipline}
          >
            <option value="">{strings.selectADiscipline}</option>
            {DISCIPLINES.map((d) => (
              <option key={d.id} value={d.en}>
                {strings.disciplineLabels[d.id]}
              </option>
            ))}
          </select>
        </div>

        <div className="space-y-2">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="rqg-type"
          >
            {strings.questionType}
          </label>
          <select
            className="w-full rounded-lg border border-border bg-background p-2.5 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="rqg-type"
            onChange={(e) => setQuestionType(e.target.value as QuestionType)}
            value={questionType}
          >
            {QUESTION_TYPES.map((qt) => (
              <option key={qt} value={qt}>
                {strings.questionTypeLabels[qt]}
              </option>
            ))}
          </select>
        </div>

        <button
          className="rounded-lg bg-primary px-6 py-2.5 font-medium text-primary-foreground text-sm transition-colors hover:bg-primary/90"
          onClick={handleGenerate}
          type="button"
        >
          {strings.generateResearchQuestions}
        </button>
      </div>
      {/* Results */}
      {results && (
        <section className="space-y-4">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.generatedResearchQuestions}
          </h2>
          <div className="space-y-4">
            {results.map((r, i) => (
              <div
                className="rounded-lg border border-border bg-muted/30 p-5"
                key={r.question}
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="text-foreground text-sm leading-relaxed">
                    <span className="font-semibold text-primary">
                      {strings.rq}
                      {i + 1}:
                    </span>{" "}
                    {r.question}
                  </p>
                  <button
                    className="shrink-0 rounded-md border border-border px-3 py-1 text-muted-foreground text-xs transition-colors hover:bg-accent hover:text-foreground"
                    onClick={() => handleCopy(i, r.question)}
                    type="button"
                  >
                    {copied === i ? strings.copied : strings.copy}
                  </button>
                </div>
                <p className="mt-2 text-muted-foreground text-xs">
                  {strings.suggestedMethodology}
                  {r.methodology}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  );
}
